/**
 * Reads Google Analytics 4 cookies (`_ga` client id, `_ga_<STREAM_ID>` session
 * id) so they can be sent with the order and forwarded to the server-side GA4
 * Measurement Protocol for purchase event matching. Returns only the cookies
 * that are present and parse cleanly; safe to call in non-browser contexts
 * (returns an empty object).
 */
export function readGaCookies(): { gaClientId?: string; gaSessionId?: string } {
  if (typeof document === 'undefined') return {}

  const read = (name: string): string | undefined => {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
    return match?.[1] === undefined ? undefined : decodeURIComponent(match[1])
  }

  const result: { gaClientId?: string; gaSessionId?: string } = {}

  // `_ga` value looks like `GA1.1.1234567890.1699999999` — the client id is
  // everything after the second dot.
  const ga = read('_ga')
  const gaMatch = ga?.match(/^GA\d+\.\d+\.(.+)$/)
  if (gaMatch) result.gaClientId = gaMatch[1]

  // Session cookie name varies per GA4 stream (`_ga_ABC123`); find the first one.
  const sessionCookieName = document.cookie
    .split(';')
    .map((part) => part.trim().split('=')[0] ?? '')
    .find((name) => name.startsWith('_ga_'))

  if (sessionCookieName) {
    const sessionValue = read(sessionCookieName)
    // Strip the common `GS<n>.<n>.` prefix shared by both known formats.
    const rest = sessionValue?.replace(/^GS\d+\.\d+\./, '')
    // New format: `s1748966267$o5$g1$t1748966366$j0$l0$h0` — digits between `s` and the first `$`.
    // Old format: `1748966267.5.1.1748966366.0.0.0` — first segment before the first `.`.
    const newFormatMatch = rest?.match(/^s(\d+)\$/)
    const oldFormatMatch = rest?.match(/^(\d+)\./)
    const sessionId = newFormatMatch?.[1] ?? oldFormatMatch?.[1]
    if (sessionId) result.gaSessionId = sessionId
  }

  return result
}
